import React, { useEffect, useRef, useState } from 'react';
import { Select, Tag, Spin } from 'antd';
import { GlobalOutlined, TagsOutlined, PlusOutlined } from '@ant-design/icons';
import { GET } from '../../utils/apiRequest';
import API from '../../config/apis';

export interface CategoryOption {
    id: string;
    name: string;
    scope: 'common' | 'specific';
    festId?: string | null;
}

interface Props {
    festId?: string | null;
    value?: string | string[];
    onChange?: (value: string | string[]) => void;
    multiple?: boolean;
    disabled?: boolean;
    placeholder?: string;
    size?: 'large' | 'middle' | 'small';
    /** Called once categories are loaded — useful for parent to read the list */
    onCategoriesLoaded?: (categories: CategoryOption[]) => void;
}

// Module-level cache: festId → CategoryOption[]
const cache = new Map<string, CategoryOption[]>();

const CategorySelect: React.FC<Props> = ({
    festId,
    value,
    onChange,
    multiple = false,
    disabled = false,
    placeholder,
    size = 'large',
    onCategoriesLoaded,
}) => {
    const [categories, setCategories] = useState<CategoryOption[]>([]);
    const [loading, setLoading] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        if (!festId) {
            setCategories([]);
            return;
        }

        if (cache.has(festId)) {
            const cached = cache.get(festId)!;
            setCategories(cached);
            onCategoriesLoaded?.(cached);
            return;
        }

        setLoading(true);
        GET(`${API.CATEGORIES_AVAILABLE}?festId=${festId}`)
            .then((res) => {
                if (!mountedRef.current) return;
                if (res.status) {
                    const raw = res.data?.data || res.data;
                    const list: CategoryOption[] = (raw?.rows || (Array.isArray(raw) ? raw : []));
                    cache.set(festId, list);
                    setCategories(list);
                    onCategoriesLoaded?.(list);
                }
            })
            .catch(() => { /* ignore */ })
            .finally(() => { if (mountedRef.current) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [festId]);

    const common = categories.filter(c => c.scope === 'common');
    const specific = categories.filter(c => c.scope !== 'common');

    const renderOption = (c: CategoryOption) => (
        <Select.Option key={c.id} value={c.id} label={c.name}>
            <div className="flex items-center gap-1.5">
                {c.scope === 'common'
                    ? <GlobalOutlined className="text-blue-400 text-[11px] shrink-0" />
                    : <TagsOutlined className="text-muted-foreground text-[11px] shrink-0" />}
                <span>{c.name}</span>
                <Tag
                    className="!ml-auto !text-[10px] !px-1 !py-0 !leading-4 !rounded-full !border-0"
                    color={c.scope === 'common' ? 'blue' : 'default'}
                >
                    {c.scope === 'common' ? 'Common' : 'Category'}
                </Tag>
            </div>
        </Select.Option>
    );

    const noFest = !festId;
    const empty = !loading && festId && categories.length === 0;

    return (
        <Select
            mode={multiple ? 'multiple' : undefined}
            value={value as any}
            onChange={onChange as any}
            disabled={disabled || noFest}
            size={size}
            showSearch
            optionFilterProp="label"
            optionLabelProp="label"
            placeholder={noFest ? 'Select festival first…' : placeholder ?? 'Select category'}
            notFoundContent={
                loading
                    ? <div className="py-2 text-center"><Spin size="small" /></div>
                    : empty
                        ? (
                            <div className="py-3 text-center text-xs text-muted-foreground space-y-1">
                                <PlusOutlined className="text-base block" />
                                <div>No categories available.</div>
                                <div>Create one first in the <strong>Categories</strong> page.</div>
                            </div>
                        )
                        : undefined
            }
            loading={loading}
            allowClear={!multiple}
            style={{ width: '100%' }}
        >
            {common.length > 0 && (
                <Select.OptGroup label="Common Categories">
                    {common.map(renderOption)}
                </Select.OptGroup>
            )}
            {specific.length > 0 && (
                <Select.OptGroup label="Categories">
                    {specific.map(renderOption)}
                </Select.OptGroup>
            )}
            {/* Fallback flat list if no groups (shouldn't normally happen) */}
            {common.length === 0 && specific.length === 0 && categories.map(renderOption)}
        </Select>
    );
};

/** Clears the cache for a specific festId (call after creating/updating categories). */
export function invalidateCategoryCache(festId?: string | null) {
    if (festId) cache.delete(festId);
    else cache.clear();
}

export default CategorySelect;
